/**
 * Word timing (T2) and sentence segments. Browser-safe, pure.
 *
 * gpt-transcribe gives the best TEXT but no timestamps; whisper-1 gives word
 * timestamps but weaker text. Each of our words takes the time of the whisper
 * word it aligns to (longest common subsequence over normalized tokens), and
 * runs of unaligned words are spread by character share between their aligned
 * neighbours. Only whisper's TIMES are used, never its text. In the test run
 * this put sentence starts within a median 0.21 s (p90 0.69 s) of the
 * reference; proportional timing inside each piece missed the p90 at 1.86 s.
 *
 * The alignment runs piece by piece against a time window of whisper words,
 * so a two-hour video never builds one 18k x 18k table.
 */

import type { TimedWord, TranscriptSegment } from "./types";

/** Lowercase, straight apostrophes, letters/digits/apostrophes only. */
export function normalizeTranscriptToken(word: string): string {
	return word
		.toLowerCase()
		.replace(/[’]/g, "'")
		.replace(/[^a-z0-9']/g, "");
}

/** Split text into words, keeping punctuation on the word. */
export function splitTranscriptWords(text: string): string[] {
	return text.split(/\s+/).filter(Boolean);
}

/** One gpt-transcribe piece: its text and where it sits in the audio. */
export interface TranscribedPiece {
	index: number;
	start: number;
	end: number;
	text: string;
}

/** One whisper-1 word with ABSOLUTE times (part offsets already added). */
export interface WhisperWord {
	word: string;
	start: number;
	end: number;
}

interface WorkingWord {
	w: string;
	n: string;
	start: number;
	end: number;
	piece: number;
	aligned: boolean;
}

/** Word times by character share over [start, end] (the T1 fallback). */
export function proportionalWordTimes(
	text: string,
	start: number,
	end: number,
): Array<{ w: string; start: number; end: number }> {
	const words = splitTranscriptWords(text);
	const totalChars =
		words.reduce((sum, w) => sum + w.length, 0) +
		Math.max(0, words.length - 1);
	const span = Math.max(0, end - start);
	const at = (pos: number) => start + (pos / Math.max(1, totalChars)) * span;
	let pos = 0;
	return words.map((w) => {
		const r = { w, start: at(pos), end: at(pos + w.length) };
		pos += w.length + 1;
		return r;
	});
}

/**
 * LCS alignment of two token arrays: for each index of `a`, the index of `b`
 * it aligns to, or -1. Keep the inputs small (one piece and its window).
 */
export function lcsAlignTokens(a: readonly string[], b: readonly string[]) {
	const n = a.length;
	const m = b.length;
	const width = m + 1;
	const dp = new Uint32Array((n + 1) * width);
	for (let i = n - 1; i >= 0; i--) {
		for (let j = m - 1; j >= 0; j--) {
			dp[i * width + j] =
				a[i] === b[j]
					? (dp[(i + 1) * width + j + 1] ?? 0) + 1
					: Math.max(
							dp[(i + 1) * width + j] ?? 0,
							dp[i * width + j + 1] ?? 0,
						);
		}
	}
	const map = new Int32Array(n).fill(-1);
	let i = 0;
	let j = 0;
	while (i < n && j < m) {
		if (a[i] === b[j]) {
			map[i] = j;
			i++;
			j++;
		} else if (
			(dp[(i + 1) * width + j] ?? 0) >= (dp[i * width + j + 1] ?? 0)
		) {
			i++;
		} else {
			j++;
		}
	}
	return { map, length: dp[0] ?? 0 };
}

const ROUND = (x: number) => Math.round(x * 1000) / 1000;

/**
 * Give every gpt-transcribe word a whisper-aligned time.
 *
 * Pieces must be in time order. `marginSec` widens each piece's whisper
 * window, because a word at a cut can be timed a little outside its piece.
 */
export function alignPiecesToWhisper(
	pieces: readonly TranscribedPiece[],
	whisperWords: readonly WhisperWord[],
	options: { marginSec?: number; durationSec?: number } = {},
): { words: TimedWord[]; aligned: number; total: number } {
	const marginSec = options.marginSec ?? 4;
	const ours: WorkingWord[] = [];
	const byPiece: WorkingWord[][] = [];
	for (const piece of pieces) {
		const list: WorkingWord[] = [];
		for (const w of proportionalWordTimes(
			piece.text,
			piece.start,
			piece.end,
		)) {
			const word = {
				...w,
				n: normalizeTranscriptToken(w.w),
				piece: piece.index,
				aligned: false,
			};
			list.push(word);
			ours.push(word);
		}
		byPiece.push(list);
	}
	const whisper = whisperWords
		.map((w) => ({ ...w, n: normalizeTranscriptToken(w.word) }))
		.filter((w) => w.n)
		.sort((x, y) => x.start - y.start);

	let aligned = 0;
	let cursor = 0;
	pieces.forEach((piece, pieceIndex) => {
		const slice = byPiece[pieceIndex] ?? [];
		if (!slice.length) return;

		let lo = cursor;
		while (
			lo < whisper.length &&
			(whisper[lo]?.start ?? 0) < piece.start - marginSec
		) {
			lo++;
		}
		let hi = lo;
		while (
			hi < whisper.length &&
			(whisper[hi]?.start ?? 0) <= piece.end + marginSec
		) {
			hi++;
		}
		if (hi <= lo) return;
		const windowWords = whisper.slice(lo, hi);
		// Empty-normalized words (pure punctuation) never match anything.
		const a = slice.map((w, k) => w.n || `∅${k}`);
		const { map } = lcsAlignTokens(
			a,
			windowWords.map((w) => w.n),
		);
		let last = -1;
		for (let k = 0; k < slice.length; k++) {
			const hit = map[k] ?? -1;
			if (hit < 0) continue;
			const ww = windowWords[hit];
			const word = slice[k];
			if (!ww || !word) continue;
			word.start = ww.start;
			word.end = Math.max(ww.start, ww.end);
			word.aligned = true;
			aligned++;
			last = hit;
		}
		if (last >= 0) cursor = lo + last + 1;
	});

	// Spread each run of unaligned words between its aligned neighbours.
	let i = 0;
	while (i < ours.length) {
		if (ours[i]?.aligned) {
			i++;
			continue;
		}
		let j = i;
		while (j < ours.length && !ours[j]?.aligned) j++;
		const run = ours.slice(i, j);
		const chars = run.reduce((sum, w) => sum + w.w.length + 1, 0);
		const prev = i > 0 ? ours[i - 1] : undefined;
		const next = j < ours.length ? ours[j] : undefined;
		// With no neighbour on one side, keep the run's own proportional span.
		const left = prev
			? prev.end
			: Math.min(
					run[0]?.start ?? 0,
					next?.start ?? Number.POSITIVE_INFINITY,
				);
		const right = next
			? next.start
			: Math.max(left, run[run.length - 1]?.end ?? left);
		const span = Math.max(0, right - left);
		let pos = 0;
		for (const w of run) {
			const len = w.w.length + 1;
			w.start = left + (span * pos) / Math.max(1, chars);
			w.end = left + (span * (pos + len - 1)) / Math.max(1, chars);
			pos += len;
		}
		i = j;
	}

	const cap = options.durationSec ?? Number.POSITIVE_INFINITY;
	const words: TimedWord[] = ours.map((w) => {
		const start = Math.min(cap, Math.max(0, w.start));
		const end = Math.min(cap, Math.max(start, w.end));
		return [w.w, ROUND(start), ROUND(end)];
	});
	return { words, aligned, total: ours.length };
}

const SENTENCE_END = /[.?!]["”')\]]*$/;

/** Whether a word ends a sentence. */
export function endsSentence(word: string): boolean {
	return SENTENCE_END.test(word);
}

/**
 * Group words into segments that start at sentence starts: a new segment
 * begins at a sentence start once the current one is at least `minSec` long,
 * after a pause of `gapSec` (once it is 8 s long), or when adding the next
 * sentence would pass `maxSec`. A run-on "sentence" longer than `maxSec` is
 * cut at a word so no segment grows without bound.
 */
export function buildTranscriptSegments(
	words: readonly TimedWord[],
	options: { minSec?: number; maxSec?: number; gapSec?: number } = {},
): TranscriptSegment[] {
	const minSec = options.minSec ?? 20;
	const maxSec = options.maxSec ?? 45;
	const gapSec = options.gapSec ?? 1.5;

	// Sentences first.
	const sentences: TimedWord[][] = [];
	let current: TimedWord[] = [];
	for (const word of words) {
		current.push(word);
		const tooLong =
			current.length > 1 &&
			(word[2] ?? 0) - (current[0]?.[1] ?? 0) > maxSec;
		if (endsSentence(word[0]) || tooLong) {
			sentences.push(current);
			current = [];
		}
	}
	if (current.length) sentences.push(current);

	const segments: TranscriptSegment[] = [];
	let group: TimedWord[] = [];
	const flush = () => {
		if (!group.length) return;
		segments.push({
			id: segments.length,
			startSec: group[0]?.[1] ?? 0,
			endSec: group[group.length - 1]?.[2] ?? 0,
			text: group.map((w) => w[0]).join(" "),
		});
		group = [];
	};
	for (const sentence of sentences) {
		if (group.length) {
			const groupStart = group[0]?.[1] ?? 0;
			const groupEnd = group[group.length - 1]?.[2] ?? 0;
			const sentenceStart = sentence[0]?.[1] ?? groupEnd;
			const sentenceEnd =
				sentence[sentence.length - 1]?.[2] ?? sentenceStart;
			const length = groupEnd - groupStart;
			const gap = sentenceStart - groupEnd;
			if (
				length >= minSec ||
				(gap >= gapSec && length >= 8) ||
				sentenceEnd - groupStart > maxSec
			) {
				flush();
			}
		}
		group.push(...sentence);
	}
	flush();
	return segments;
}

/** The transcript as one line of text. */
export function timedWordsToText(words: readonly TimedWord[]): string {
	return words.map((w) => w[0]).join(" ");
}
